import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FlaskConical, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toastApiError } from '@/lib/errors';
import EmptyState from '@/components/EmptyState';
import { invalidateLaboratories } from '@/hooks/useLaboratories';
import { useDsiUnits, dsiLabel } from '@/lib/dsiUnits';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Create/edit dialog laboratoře. `code` je neměnný (po vytvoření disabled).
 * DCC jednotka z řízeného číselníku (GET /api/dsi-units); barva color-picker + hex.
 */
function LaboratoryDialog({ open, onClose, item, onSaved }) {
  const dsiUnits = useDsiUnits();
  const isEdit = !!item;
  const [form, setForm] = useState({ code: '', name: '', certPrefix: '', dccUnit: '', color: '#2563eb' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(item
      ? {
          code: item.code || '',
          name: item.name || '',
          certPrefix: item.certPrefix || '',
          dccUnit: item.dccUnit || '',
          color: item.color || '#2563eb',
        }
      : { code: '', name: '', certPrefix: '', dccUnit: '', color: '#2563eb' });
  }, [open, item]);

  const handleSave = async () => {
    if (!isEdit && !form.code.trim()) { toast.error('Vyplňte kód'); return; }
    if (!form.name.trim()) { toast.error('Vyplňte název'); return; }
    if (!form.certPrefix.trim()) { toast.error('Vyplňte prefix certifikátu'); return; }
    if (!form.dccUnit) { toast.error('Vyberte DCC jednotku'); return; }
    if (!HEX_RE.test(form.color)) { toast.error('Barva musí být hex (#RRGGBB)'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/laboratories/${item.code}`, {
          name: form.name.trim(),
          certPrefix: form.certPrefix.trim(),
          dccUnit: form.dccUnit,
          color: form.color,
        });
        toast.success('Upraveno');
      } else {
        await api.post('/laboratories', {
          code: form.code.trim(),
          name: form.name.trim(),
          certPrefix: form.certPrefix.trim(),
          dccUnit: form.dccUnit,
          color: form.color,
        });
        toast.success('Vytvořeno');
      }
      invalidateLaboratories();
      onSaved();
      onClose();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se uložit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', maxWidth: '480px' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--ct-accent)' }}>
            {isEdit ? 'Upravit laboratoř' : 'Nová laboratoř'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="ct-form-label">Kód (slug, neměnný)</label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="delka"
              disabled={isEdit}
              title={isEdit ? 'Kód nelze měnit po vytvoření' : 'lowercase slug bez diakritiky, např. delka'}
            />
          </div>
          <div>
            <label className="ct-form-label">Název</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Délka"
            />
          </div>
          <div>
            <label className="ct-form-label">Prefix certifikátu</label>
            <Input
              value={form.certPrefix}
              onChange={(e) => setForm({ ...form, certPrefix: e.target.value })}
              placeholder="DE"
              className="font-mono"
            />
          </div>
          <div>
            <label className="ct-form-label">DCC jednotka (D-SI)</label>
            <select
              value={form.dccUnit}
              onChange={(e) => setForm({ ...form, dccUnit: e.target.value })}
              className="w-full h-9 px-2 rounded border text-sm"
              style={{ background: 'var(--ct-bg)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}
            >
              <option value="">— vyberte —</option>
              {dsiUnits.map((u) => (
                <option key={u.symbol} value={u.symbol}>{u.label}</option>
              ))}
            </select>
            <p className="text-xs mt-1" style={{ color: 'var(--ct-text-disabled)' }}>
              SI jednotka veličiny do DCC certifikátu (řízený číselník — ne volný text).
            </p>
          </div>
          <div>
            <label className="ct-form-label">Barva</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={HEX_RE.test(form.color) ? form.color : '#2563eb'}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-9 h-9 rounded border cursor-pointer"
                style={{ borderColor: 'var(--ct-border)', background: 'transparent' }}
              />
              <Input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#2563eb"
                className="h-9 text-sm font-mono w-32"
                style={form.color && !HEX_RE.test(form.color) ? { borderColor: 'var(--ct-error)' } : undefined}
              />
            </div>
            {form.color && !HEX_RE.test(form.color) && (
              <p className="text-xs mt-1" style={{ color: 'var(--ct-error)' }}>
                Neplatný formát barvy — zadejte hex, např. #2563eb
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="ct-btn-secondary" disabled={saving}>Zrušit</Button>
          <Button onClick={handleSave} className="ct-btn-primary" disabled={saving}>
            {saving ? 'Ukládám…' : (isEdit ? 'Uložit' : 'Vytvořit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LaboratoriesPage() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/laboratories')
      .then((r) => setLabs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLabs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (lab) => {
    if (!window.confirm(
      `Smazat laboratoř „${lab.name}"? Laboratoř s vázanými metodikami / etalony / kalibracemi nelze smazat.`
    )) return;
    try {
      await api.delete(`/laboratories/${lab.code}`);
      toast.success('Laboratoř smazána');
      invalidateLaboratories();
      load();
    } catch (err) {
      toastApiError(err, 'Nepodařilo se smazat');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="ct-h1 flex items-center gap-2">
            <FlaskConical className="w-5 h-5" style={{ color: 'var(--ct-accent)' }} />
            Laboratoře
          </h1>
          <p className="ct-page-subtitle">
            Kalibrační veličiny — barva, prefix certifikátu, DCC jednotka
          </p>
        </div>
        <Button
          onClick={() => { setEditingItem(null); setDialogOpen(true); }}
          className="ct-btn-primary gap-1 text-xs"
        >
          <Plus className="w-3.5 h-3.5" /> Nová laboratoř
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-sm" style={{ color: 'var(--ct-text-disabled)' }}>Načítám…</div>
      ) : labs.length === 0 ? (
        <EmptyState
          icon={FlaskConical}
          title="Žádné laboratoře"
          description="Přidejte první laboratoř tlačítkem nahoře."
        />
      ) : (
        <div className="ct-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--ct-bg-secondary)' }}>
                <th className="text-left py-2 px-3 ct-eyebrow">Kód</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Název</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Prefix</th>
                <th className="text-left py-2 px-3 ct-eyebrow">DCC jednotka</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Barva</th>
                <th className="text-left py-2 px-3 ct-eyebrow">Typ</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {labs.map((lab) => (
                <tr key={lab.code} style={{ borderTop: '1px solid var(--ct-border)' }}>
                  <td className="py-2 px-3 font-mono text-xs">{lab.code}</td>
                  <td className="py-2 px-3">{lab.name}</td>
                  <td className="py-2 px-3 font-mono text-xs">{lab.certPrefix || '—'}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
                    {lab.dccUnit ? dsiLabel(lab.dccUnit) : '—'}
                  </td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        style={{
                          background: lab.color, width: 14, height: 14, borderRadius: 4,
                          display: 'inline-block', border: '1px solid var(--ct-border)',
                        }}
                      />
                      <span className="font-mono text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{lab.color}</span>
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    {lab.isBuiltin ? (
                      <span
                        className="text-[10px] font-bold rounded px-1.5 py-0.5"
                        style={{ background: 'var(--ct-bg-secondary)', color: 'var(--ct-text-secondary)' }}
                        title="Vestavěná laboratoř"
                      >
                        Vestavěná
                      </span>
                    ) : (
                      <span
                        className="text-[10px] font-bold rounded px-1.5 py-0.5"
                        style={{ background: 'var(--ct-accent)', color: '#fff' }}
                      >
                        Vlastní
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => { setEditingItem(lab); setDialogOpen(true); }}
                        title="Upravit"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-text-secondary)' }}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(lab)}
                        title="Smazat"
                        className="p-1 rounded transition-opacity hover:opacity-70"
                        style={{ color: 'var(--ct-error)' }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LaboratoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        item={editingItem}
        onSaved={load}
      />
    </div>
  );
}
